/**
 * Server-backed VIN decode / enrichment client.
 *
 * The browser never talks to MarketCheck (or any paid provider) directly —
 * credentials stay on the server, and both endpoints are authenticated +
 * rate-limited. Local VIN format / check-digit helpers still live in
 * `vinDecoder.ts`.
 */

import type { VinDecodedData } from "./vinDecoder";

export type VehicleIdentity = {
  vin: string;
  year: number;
  make: string;
  model: string;
  trim: string | null;
  version: string | null;
  styleKey: string | null;
  confidenceRaw: string | null;
  confidenceScore: number | null;
  specs: {
    bodyStyle: string | null;
    bodySubtype: string | null;
    driveType: string | null;
    transmission: string | null;
    engine: string | null;
    fuelType: string | null;
    powertrainType: string | null;
    doors: number | null;
    seating: number | null;
    cityMpg: number | null;
    highwayMpg: number | null;
  };
  enrichment: {
    exteriorColor: string | null;
    interiorColor: string | null;
    msrp: number | null;
    installedOptions: string[];
  };
  provider: {
    name: string | null;
    tier: string | null;
    decodedAt: string;
  };
};

export type VinApiSuccess = {
  ok: true;
  identity: VehicleIdentity;
  displayTitle: string | null;
  cached: boolean;
  locked: string[];
  suggested?: string[];
};

export type VinApiFailure = {
  ok: false;
  errorCode: string;
  errorMessage: string;
  status: number;
};

export type VinApiResult = VinApiSuccess | VinApiFailure;

function vinEndpoint(path: "/vin/decode" | "/vin/enrich"): string {
  const graphql =
    process.env.REACT_APP_GRAPHQL_ENDPOINT || "http://localhost:8081/graphql";
  if (graphql.endsWith("/graphql")) {
    return graphql.slice(0, -"/graphql".length) + path;
  }
  return graphql.replace(/\/$/, "") + path;
}

function authHeader(): Record<string, string> {
  const token = localStorage.getItem("token");
  return token ? { Authorization: `Bearer ${token}` } : {};
}

async function postVin(
  path: "/vin/decode" | "/vin/enrich",
  vin: string
): Promise<VinApiResult> {
  let res: Response;
  try {
    res = await fetch(vinEndpoint(path), {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
        ...authHeader(),
      },
      body: JSON.stringify({ vin: vin.toUpperCase() }),
    });
  } catch {
    return {
      ok: false,
      errorCode: "NETWORK_ERROR",
      errorMessage: "Failed to reach the VIN decoder service.",
      status: 0,
    };
  }

  let body: any = null;
  try {
    body = await res.json();
  } catch {
    body = null;
  }

  if (!res.ok) {
    const err = body?.errors?.[0];
    return {
      ok: false,
      errorCode: err?.extensions?.code || "API_ERROR",
      errorMessage:
        err?.message || "We could not find a vehicle associated with this VIN.",
      status: res.status,
    };
  }

  return {
    ok: true,
    identity: body.identity as VehicleIdentity,
    displayTitle: body.displayTitle ?? null,
    cached: Boolean(body.cached),
    locked: Array.isArray(body.locked) ? body.locked : [],
    suggested: Array.isArray(body.suggested) ? body.suggested : undefined,
  };
}

/** Identity decode (MarketCheck Basic via the server). */
export function decodeVinViaServer(vin: string): Promise<VinApiResult> {
  return postVin("/vin/decode", vin);
}

/** Enrichment decode (MarketCheck NeoVIN via the server). Optional. */
export function enrichVinViaServer(vin: string): Promise<VinApiResult> {
  return postVin("/vin/enrich", vin);
}

/**
 * Map a server identity onto the legacy `VinDecodedData` shape so existing
 * wizard state / helpers keep working without a big rename.
 */
export function identityToVinDecodedData(
  identity: VehicleIdentity
): VinDecodedData {
  return {
    year: identity.year,
    make: identity.make || "",
    model: identity.model || "",
    series: "",
    series2: "",
    trim: identity.trim || "",
    trim2: "",
    bodyStyle: identity.specs?.bodyStyle || "",
    driveType: identity.specs?.driveType || "",
    fuelType: identity.specs?.fuelType || "",
    transmission: identity.specs?.transmission || "",
    engineCylinders: "",
    engineDisplacement: "",
    engineDescription: identity.specs?.engine || "",
    doors: identity.specs?.doors != null ? String(identity.specs.doors) : "",
    errorCode: "",
    errorText: "",
    isValid: true,
  };
}

/**
 * Prefer trim; fall back to version when trim is a generic placeholder
 * (mirrors server `pickTrimLabel`).
 */
export function pickDisplayTrim(identity: VehicleIdentity): string {
  const trim = (identity.trim || "").trim();
  const version = (identity.version || "").trim();
  const generic = new Set(["", "-", "base", "standard", "base / standard", "n/a", "na"]);
  if (trim && !generic.has(trim.toLowerCase())) return trim;
  if (version && !generic.has(version.toLowerCase())) return version;
  return trim || version || "";
}
